import PropTypes from "prop-types";
import ReactSelect from "react-select";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import CreatableSelect from "react-select/creatable";
import { useContext } from "react";
import AppContext from "../services/context";

const Select = ({
  formik,
  name,
  label,
  description,
  placeholder,
  required,
  creatable,
  async,
  options,
  onBlur,
  onChange,
  isMulti,
  isOptionDisabled,
  onCreateOption,
  onCreateTerm,
  disabled,
  menuIsOpen,
  components,
}) => {
  const { themeKey } = useContext(AppContext);
  const isDarkTheme = themeKey === 'dark';

  const selectStyles = {
    control: (baseStyles, state) => ({
      ...baseStyles,
      borderRadius: "0.5rem",
      fontSize: "0.875rem",
      minHeight: "38px",
      borderColor: state.isFocused 
        ? (isDarkTheme ? "#71B257" : "#156D2D")
        : (isDarkTheme ? "#374151" : "#d1d5db"),
      backgroundColor: isDarkTheme ? "#1f2937" : "#ffffff",
      boxShadow: state.isFocused 
        ? (isDarkTheme ? "0 0 0 2px rgba(113, 178, 87, 0.2)" : "0 0 0 2px rgba(21, 109, 45, 0.2)")
        : "none",
      opacity: state.isDisabled ? "0.5" : "1",
      "&:hover": {
        borderColor: isDarkTheme ? "#71B257" : "#156D2D",
      },
    }),
    option: (baseStyles, state) => ({
      ...baseStyles,
      fontSize: "0.875rem",
      backgroundColor: state.isSelected 
        ? (isDarkTheme ? "#156D2D" : "#71B257")
        : state.isFocused
        ? (isDarkTheme ? "#374151" : "#f3f4f6")
        : (isDarkTheme ? "#1f2937" : "#ffffff"),
      color: state.isSelected
        ? "#ffffff"
        : (isDarkTheme ? "#f5f5f5" : "#111111"),
      cursor: "pointer",
      "&:active": {
        backgroundColor: isDarkTheme ? "#156D2D" : "#71B257",
      },
    }),
    multiValue: (baseStyles) => ({
      ...baseStyles,
      borderRadius: "0.375rem",
      backgroundColor: isDarkTheme ? "#156D2D" : "#71B257",
    }),
    multiValueLabel: (baseStyles) => ({
      ...baseStyles,
      fontSize: "0.875rem",
      color: "#ffffff",
      fontWeight: "500",
    }),
    multiValueRemove: (baseStyles) => ({
      ...baseStyles,
      color: "#ffffff",
      "&:hover": {
        backgroundColor: isDarkTheme ? "#0f5221" : "#5a8f44",
        color: "#ffffff",
      },
    }),
    menu: (baseStyles) => ({
      ...baseStyles,
      borderRadius: "0.5rem",
      backgroundColor: isDarkTheme ? "#1f2937" : "#ffffff",
      border: isDarkTheme ? "1px solid #374151" : "1px solid #d1d5db",
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    }),
    menuList: (baseStyles) => ({
      ...baseStyles,
      padding: "0.25rem",
    }),
    input: (baseStyles) => ({
      ...baseStyles,
      color: isDarkTheme ? "#f5f5f5" : "#111111",
    }),
    placeholder: (baseStyles) => ({
      ...baseStyles,
      color: isDarkTheme ? "#9ca3af" : "#6b7280",
      fontSize: "0.875rem",
    }),
    singleValue: (baseStyles) => ({
      ...baseStyles,
      color: isDarkTheme ? "#f5f5f5" : "#111111",
    }),
    dropdownIndicator: (baseStyles) => ({
      ...baseStyles,
      color: isDarkTheme ? "#71B257" : "#156D2D",
      "&:hover": {
        color: isDarkTheme ? "#8fc973" : "#0f5221",
      },
    }),
    clearIndicator: (baseStyles) => ({
      ...baseStyles,
      color: isDarkTheme ? "#71B257" : "#156D2D",
      "&:hover": {
        color: isDarkTheme ? "#8fc973" : "#0f5221",
      },
    }),
    indicatorSeparator: (baseStyles) => ({
      ...baseStyles,
      backgroundColor: isDarkTheme ? "#374151" : "#d1d5db",
    }),
  };

  const SelectComponent =
    creatable && !async
      ? CreatableSelect
      : async && creatable
      ? AsyncCreatableSelect
      : async
      ? AsyncSelect
      : ReactSelect;

  const getValue = (obj, path) => path.split('.').reduce((acc, part) => acc && acc[part], obj);
  const value = getValue(formik.values, name);

  return (
    <div className="inline-block mr-1">
      <label htmlFor={name} className="block mb-1">
        {label || placeholder} {required && <span className="text-red-500">*</span>}
      </label>
      {description && <p className="text-xs mb-1">{description}</p>}
      <SelectComponent
        menuPosition="fixed"
        placeholder={
          isMulti ? "Selecione uma ou mais opções" : "Selecione uma opção"
        }
        styles={selectStyles}
        id={name}
        name={name}
        options={!async && options}
        loadOptions={async && options}
        cacheOptions={async && true}
        components={components}
        defaultOptions={async && true}
        formatCreateLabel={(inputValue) =>
          `Clique para Adicionar "${inputValue}"`
        }
        onCreateOption={
          onCreateOption &&
          creatable &&
          ((value) => {
            onCreateOption(value);
            if (isMulti) {
              if (Array.isArray(formik.values[name])) {
                formik.setFieldValue(name, [...formik.values[name], value]);
              } else {
                formik.setFieldValue(name, [value]);
              }
            } else {
              formik.setFieldValue(name, value);
            }
            formik.setFieldTouched(name, true, true);
          })
        }
        value={value}
        onChange={(e) => {
          if (onChange) onChange(e);
          if(Array.isArray(e)) {
            if(typeof e[e.length - 1].value === 'string') 
              e[e.length - 1] = {...e[e.length - 1], value: {[onCreateTerm]: e[e.length - 1].value}};
            formik.setFieldValue(name, e.reduce((acc, curr) => [...acc, curr ], []));
          }else{
            formik.setFieldValue(name, e);
          }
          formik.setFieldTouched(name, true, true);
        }}
        onBlur={(e) => {
          if (onBlur) onBlur(e);
        }}
        isMulti={isMulti}
        isOptionDisabled={isOptionDisabled}
        isDisabled={disabled}
        menuIsOpen={menuIsOpen || undefined}
      />
      {formik?.errors[name] &&
        (formik.touched[name] || formik.submitCount > 0) && (
          <span className="block text-red-500 text-xs">{formik.errors[name]}</span>
        )}
    </div>
  );
};

Select.propTypes = {
  formik: PropTypes.object,
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  creatable: PropTypes.bool,
  async: PropTypes.bool,
  disabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  menuIsOpen: PropTypes.bool,
  options: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  isOptionDisabled: PropTypes.func,
  onCreateOption: PropTypes.func,
  onCreateTerm: PropTypes.string,
  description: PropTypes.string || PropTypes.node,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  components: PropTypes.object
};

Select.defaultProps = {
  placeholder: "Selecione um item",
  required: false,
  creatable: false,
  onCreateTerm: "name",
  isMulti: false,
  async: false,
  disabled: false,
  menuIsOpen: false,
  components: {},
  options: [],
};

export default Select;